import React, { useEffect, useRef } from 'react';
import BasePage from '../components/BasePage';
import { format } from 'date-fns';
import Tooltip from '../components/Tooltip';
import FieldsGroup from '../components/FieldsGroup';
import Subtitle from '../components/Subtitle';

const Paiements = (props) => {
    const nextPaymentRef = useRef(null);
    const containerRef = useRef(null);

    let impayes = 0;

    useEffect(() => {
        // Crée un intervalle qui se répète toutes les 100ms
        const intervalId = setInterval(() => {
            if (nextPaymentRef.current) {
                // Récupère les dimensions du conteneur et de l'élément "prochain paiement"
                const containerRect = containerRef.current.getBoundingClientRect();
                const nextPaymentRect = nextPaymentRef.current.getBoundingClientRect();

                // Calcule la position horizontale pour centrer l'élément
                const scrollLeft = nextPaymentRect.left - containerRect.left + containerRef.current.scrollLeft - (containerRect.width / 2) + (nextPaymentRect.width / 2);
                // Définit le défilement pour centrer l'élément
                containerRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                clearInterval(intervalId);
            }
        }, 100);

        return () => clearInterval(intervalId);
    }, []); // Exécute l'effet une seule fois après le montage

    const fields_groups = [
        {
            key: 'infos',
            name: 'Informations sur les paiements',
            icon: 'fa-solid fa-hand-holding-dollar',
            fields: [
                { key: 'start', name: <>Date du 1<sup>er</sup> prélèvement</>, format: 'date' },
                { key: 'end', name: 'Date du dernier prélèvement', format: 'date' },
                { key: 'p_done', name: 'Mensualités déjà réglées' },
                { key: 'p_remain', name: 'Mensualités restantes' },
            ]
        },
        {
            key: 'impayes',
            name: 'Impayés',
            icon: 'fa-solid fa-circle-exclamation',
            hide_if_empty: true,
            fields: [
                { key: 'nb_impayes', name: 'Nombre d\'impayés' },
                { key: 'dates_impayes', name: 'Dates des impayés' },
                { key: 'montant_impayes', name: 'Montant des impayes', format: 'money' },
            ]
        }
    ];

    const content = (data) => {
        console.log("donnée paiement" . data);

        // Création d'une liste combinée de tous les paiements
        let payments = [...data.payments];

        if (data.infos.is_out) {
            payments.push({
                date: data.infos.date_out,
                is_out_event: true,
                is_next: true,
            });
        }

        // Trier les paiements par ordre chronologique
        payments.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Calcul de la date limite pour les impayés
        const dateOut = data.infos.is_out ? new Date(data.infos.date_out) : null;

        return (
            <div id="paiements" className="mt-4">
                <div id="infos" className="mb-8">
                    <FieldsGroup fields_groups={fields_groups} data={data} />
                </div>

                <div id="echeancier" className="px-4">
                    <Subtitle icon="fa-solid fa-list-check">Mon échéancier</Subtitle>

                    <div className="my-10 -mx-8 fadeIn-white-wrapper">
                        <div className="flex flex-row py-10 relative overflow-x-auto px-[15%]" ref={containerRef}>
                            {/* Affichage des paiements et autres événements */}
                            {payments.map((event, index, array) => (
                                <React.Fragment key={index}>
                                    {/* Ligne horizontale entre les badges */}
                                    {index > 0 && index < array.length && (
                                        <div className="flex items-center">
                                            <div className="w-14 h-0.5 bg-gray-300"></div>
                                        </div>
                                    )}

                                    <div className={`flex flex-col items-center py-2 gap-6`}>
                                        {/* Affichage spécial pour "Contrat out" */}
                                        {event.is_out_event && (
                                            <>
                                                <Tooltip className="top-0" text="Contrat out" color="mdark" arrowPosition="bottom"></Tooltip>
                                                <div className="flex justify-center">
                                                    <div className="rounded-full px-3 pt-0.5 py-1 text-sm text-white bg-red-500"
                                                     ref={event.is_next ? nextPaymentRef : null}>
                                                        {format(event.date, 'dd/MM/yyyy')}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {/* Gestion des impayés */}
                                        {!event.is_out_event && (() => {
                                            const eventDate = new Date(event.date);
                                            const isImpayes = data.impayes?.dates_impayes?.includes(format(eventDate, 'dd/MM/yyyy'));

                                            // Si l'impayé est après `date_out`, supprimer complètement l'affichage
                                            if (isImpayes && dateOut && eventDate > dateOut) {
                                                return null;
                                            }

                                            // Affichage des impayés avant `date_out`
                                            if (isImpayes) {
                                                impayes++;
                                                return (
                                                    <>
                                                        <Tooltip className="top-0" text="Impayé" color="mdark" arrowPosition="bottom"></Tooltip>
                                                        <div className="flex justify-center">
                                                        <div className="rounded-full px-3 pt-0.5 py-1 text-sm text-white bg-red-500"
                                                            ref={event.is_out_event ? nextPaymentRef : null} 
                                                        >
                                                                {format(eventDate, 'dd/MM/yyyy')}
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            }
                                            return null;
                                        })()}

                                    {/* Gestion des paiements standards */}
                                    {!event.is_out_event && !(data.impayes?.dates_impayes?.includes(format(new Date(event.date), 'dd/MM/yyyy'))) && (
                                        (() => {
                                            const eventDate = new Date(event.date);

                                            // Exclure complètement les paiements après `date_out`
                                            if (dateOut && eventDate > dateOut) {
                                                return null;
                                            }

                                            // Chercher la dernière date d'impayé
                                            const lastImpayDate = data.impayes?.dates_impayes
                                                ?.map(date => new Date(date.split('/').reverse().join('-'))) // Convertir les dates en objets Date
                                                .sort((a, b) => b - a)[0]; // Prendre la plus récente

                                            // Vérifier si le paiement est entre la dernière date d'impayé et `dateOut`
                                            const isBetweenImpayAndOut = lastImpayDate && dateOut && eventDate > lastImpayDate && eventDate <= dateOut;

                                            if (isBetweenImpayAndOut) {
                                                impayes++;
                                                return (
                                                    <>
                                                        <Tooltip className="top-0" text="Impayé" color="mdark" arrowPosition="bottom"></Tooltip>
                                                        <div className="flex justify-center">
                                                            <div className="rounded-full px-3 pt-0.5 py-1 text-sm text-white bg-red-500">
                                                                {format(eventDate, 'dd/MM/yyyy')}
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            }

                                            // Sinon affichage normal des paiements standards
                                            return (
                                                <div className="flex justify-center">
                                                    {/* Affichage du Tooltip pour le "Prochain paiement" */}
                                                    {event.is_next && (
                                                        <Tooltip
                                                            className="top-0"
                                                            text="Prochain paiement"
                                                            color="mdark"
                                                            arrowPosition="bottom"
                                                        />
                                                    )}
                                                    {/* Affichage du badge avec la date */}
                                                    <div className={`rounded-full px-3 pt-0.5 py-1 text-sm text-white
                                                        ${event.done
                                                            ? 'bg-mlime'   // Paiement effectué
                                                            : event.is_next
                                                                ? 'bg-morange' // Prochain paiement
                                                                : 'bg-cyan-500' // Autres paiements à venir
                                                        }`}
                                                        ref={event.is_next ? nextPaymentRef : null}
                                                    >
                                                        {format(eventDate, 'dd/MM/yyyy')}
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    )}

                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return <BasePage content={content} title={props.title ?? null} />;
};

export default Paiements;
